import React, {ReactNode} from 'react'
import {Link as RouteLink} from 'react-router-dom'
import {Box, Breadcrumb as ChakraBreadcrumb, BreadcrumbItem, BreadcrumbLink, Skeleton} from '@chakra-ui/react'

interface BreadcrumbCategory {
    id: string
    htmlValue: string | ReactNode
    url?: string
}

interface BreadcrumbProps {
    items?: Array<BreadcrumbCategory>
}

/**
 * @name Breadcrumb
 *
 * @component
 *
 * @description
 * Renders a breadcrumb navigation using Chakra UI components.
 *
 * @example
 * ```typescript
 * const items = [
 *   { id: '1', url: '/home', htmlValue: 'Home' },
 *   { id: '2', url: '/about', htmlValue: 'About' },
 *   { id: '3', url: '/contact', htmlValue: 'Contact' }
 * ]
 * <Breadcrumb items={items} />
 * ```
 *
 * @param {BreadcrumbProps} props - The props for the Breadcrumb component.
 * @param {Array<BreadcrumbCategory>} [props.items] - An array of breadcrumb items.
 * @param {string} props.items[].id - The unique identifier for each breadcrumb item.
 * @param {string} props.items[].url - The URL to navigate to when the breadcrumb item is clicked.
 * @param {string} props.items[].htmlValue - The display name of the breadcrumb item.
 *
 * @returns {JSX.Element} - Rendered Breadcrumb component.
 */
const Breadcrumb = ({items}: BreadcrumbProps): JSX.Element => {
    return (
        <ChakraBreadcrumb separator={<Box aria-hidden="true">{'/'}</Box>}>
            {items?.map((item, index) => (
                <BreadcrumbItem key={`${item.id}-${index}`} isCurrentPage={index === items.length - 1}>
                    <BreadcrumbLink as={RouteLink} to={item.url || ''}>
                        {item.htmlValue}
                    </BreadcrumbLink>
                </BreadcrumbItem>
            ))}
        </ChakraBreadcrumb>
    )
}

/**
 * @name BreadcrumbSkeleton
 *
 * @component
 *
 * @description
 * Renders skeleton breadcrumb for lazy loading.
 *
 * @returns {JSX.Element} - Rendered BreadcrumbSkeleton component.
 */
const BreadcrumbSkeleton = (): JSX.Element => {
    return (
        <ChakraBreadcrumb data-testid="breadcrumb-skeleton">
            <BreadcrumbItem>
                <Skeleton height="20px" width="100px" />
            </BreadcrumbItem>
            <BreadcrumbItem>
                <Skeleton height="20px" width="100px" />
            </BreadcrumbItem>
            <BreadcrumbItem>
                <Skeleton height="20px" width="100px" />
            </BreadcrumbItem>
        </ChakraBreadcrumb>
    )
}

export {Breadcrumb, BreadcrumbSkeleton}
